//frontend/src/components/gro/RiskInventoryItemForm.tsx
import { useEffect, useMemo, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import type {
  RiskInventoryItemCreateIn,
  RiskInventoryItemUpdateIn,
  RiskInventoryLookupOption,
  RiskMatrixLevelOption,
} from "@/types/riskInventory";

type ItemFormValue = RiskInventoryItemCreateIn | RiskInventoryItemUpdateIn;

type Props = {
  open: boolean;
  mode: "create" | "edit";
  value: ItemFormValue;
  saving?: boolean;
  risks: RiskInventoryLookupOption[];
  dimensions: RiskInventoryLookupOption[];
  probabilityLevels: RiskMatrixLevelOption[];
  severityLevels: RiskMatrixLevelOption[];
  units: RiskInventoryLookupOption[];
  departments: RiskInventoryLookupOption[];
  jobPositions: RiskInventoryLookupOption[];
  workerGroups: RiskInventoryLookupOption[];
  campaigns: RiskInventoryLookupOption[];
  onOpenChange: (open: boolean) => void;
  onChange: (next: ItemFormValue) => void;
  onSubmit: () => void;
};

function findNameById(options: RiskInventoryLookupOption[], id?: string | null) {
  if (!id) return "";
  return options.find((item) => item.id === id)?.name ?? "";
}

function buildSuggestedTitle(
  value: ItemFormValue,
  risks: RiskInventoryLookupOption[],
  units: RiskInventoryLookupOption[],
  departments: RiskInventoryLookupOption[],
  jobPositions: RiskInventoryLookupOption[],
  workerGroups: RiskInventoryLookupOption[],
) {
  const riskName = findNameById(risks, value.risk_catalog_id).trim();

  let cutName = "";
  if (value.unit_id) cutName = findNameById(units, value.unit_id);
  else if (value.department_id) cutName = findNameById(departments, value.department_id);
  else if (value.job_position_id) cutName = findNameById(jobPositions, value.job_position_id);
  else if (value.worker_group_id) cutName = findNameById(workerGroups, value.worker_group_id);

  if (!riskName) return "";
  return `${riskName} - ${cutName || "Geral"}`;
}

function optionLabelWithScore(option: RiskMatrixLevelOption) {
  return `${option.name} (${option.score})`;
}

function clearOtherCuts(
  next: ItemFormValue,
  currentField: "unit_id" | "department_id" | "job_position_id" | "worker_group_id",
  selectedValue: string,
) {
  const valueOrNull = selectedValue || null;
  return {
    ...next,
    unit_id: currentField === "unit_id" ? valueOrNull : null,
    department_id: currentField === "department_id" ? valueOrNull : null,
    job_position_id: currentField === "job_position_id" ? valueOrNull : null,
    worker_group_id: currentField === "worker_group_id" ? valueOrNull : null,
  };
}

export function RiskInventoryItemForm({
  open,
  mode,
  value,
  saving = false,
  risks,
  dimensions,
  probabilityLevels,
  severityLevels,
  units,
  departments,
  jobPositions,
  workerGroups,
  campaigns,
  onOpenChange,
  onChange,
  onSubmit,
}: Props) {
  const [titleEditedManually, setTitleEditedManually] = useState(false);

  const suggestedTitle = useMemo(
    () => buildSuggestedTitle(value, risks, units, departments, jobPositions, workerGroups),
    [value, risks, units, departments, jobPositions, workerGroups],
  );

  useEffect(() => {
    if (!open) return;
    setTitleEditedManually(Boolean(value.title?.trim()) && value.title.trim() !== suggestedTitle);
  }, [open, mode]);

  useEffect(() => {
    if (!open || titleEditedManually || !suggestedTitle) return;
    if (value.title !== suggestedTitle) {
      onChange({ ...value, title: suggestedTitle });
    }
  }, [open, suggestedTitle, titleEditedManually, value, onChange]);

  function updateField<K extends keyof ItemFormValue>(key: K, nextValue: ItemFormValue[K]) {
    onChange({
      ...value,
      [key]: nextValue,
    });
  }

  function handleTitleChange(nextTitle: string) {
    setTitleEditedManually(true);
    onChange({ ...value, title: nextTitle });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[90vh] max-h-[90vh] w-[95vw] max-w-4xl flex-col overflow-hidden p-0">
        <div className="shrink-0 border-b bg-background px-6 py-4">
          <DialogHeader>
            <DialogTitle>{mode === "create" ? "Novo item do inventário" : "Editar item do inventário"}</DialogTitle>
            <DialogDescription>
              Informe a dimensão, o risco, a campanha opcional e a classificação manual do item do inventário.
            </DialogDescription>
          </DialogHeader>
        </div>
  
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <Label>Dimensão *</Label>
              <Select value={value.dimension_id || undefined} onValueChange={(nextValue) => updateField("dimension_id", nextValue)}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Selecione a dimensão" />
                </SelectTrigger>
                <SelectContent>
                  {dimensions.map((item) => (
                    <SelectItem key={item.id} value={item.id}>
                      {item.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
  
            <div>
              <Label>Risco *</Label>
              <Select value={value.risk_catalog_id || undefined} onValueChange={(nextValue) => updateField("risk_catalog_id", nextValue)}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Selecione o risco" />
                </SelectTrigger>
                <SelectContent>
                  {risks.map((item) => (
                    <SelectItem key={item.id} value={item.id}>
                      {item.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
  
            <div>
              <Label>Probabilidade *</Label>
              <Select value={value.probability_level_id || undefined} onValueChange={(nextValue) => updateField("probability_level_id", nextValue)}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Selecione a probabilidade" />
                </SelectTrigger>
                <SelectContent>
                  {probabilityLevels.map((item) => (
                    <SelectItem key={item.id} value={item.id}>
                      {optionLabelWithScore(item)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
  
            <div>
              <Label>Severidade *</Label>
              <Select value={value.severity_level_id || undefined} onValueChange={(nextValue) => updateField("severity_level_id", nextValue)}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Selecione a severidade" />
                </SelectTrigger>
                <SelectContent>
                  {severityLevels.map((item) => (
                    <SelectItem key={item.id} value={item.id}>
                      {optionLabelWithScore(item)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
  
            <div className="md:col-span-2">
              <Label>Campanha</Label>
              <Select
                value={value.campaign_id ?? "__none__"}
                onValueChange={(nextValue) =>
                  updateField("campaign_id", (nextValue === "__none__" ? null : nextValue) as ItemFormValue["campaign_id"])
                }
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Selecione uma campanha encerrada" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Não vinculada</SelectItem>
                  {campaigns.map((item) => (
                    <SelectItem key={item.id} value={item.id}>
                      {item.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
  
            <div>
              <Label>Unidade</Label>
              <Select
                value={value.unit_id ?? "__none__"}
                onValueChange={(nextValue) => onChange(clearOtherCuts(value, "unit_id", nextValue === "__none__" ? "" : nextValue))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Selecione a unidade" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Não informado</SelectItem>
                  {units.map((item) => (
                    <SelectItem key={item.id} value={item.id}>
                      {item.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
  
            <div>
              <Label>Departamento</Label>
              <Select
                value={value.department_id ?? "__none__"}
                onValueChange={(nextValue) =>
                  onChange(clearOtherCuts(value, "department_id", nextValue === "__none__" ? "" : nextValue))
                }
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Selecione o departamento" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Não informado</SelectItem>
                  {departments.map((item) => (
                    <SelectItem key={item.id} value={item.id}>
                      {item.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
  
            <div>
              <Label>Cargo</Label>
              <Select
                value={value.job_position_id ?? "__none__"}
                onValueChange={(nextValue) =>
                  onChange(clearOtherCuts(value, "job_position_id", nextValue === "__none__" ? "" : nextValue))
                }
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Selecione o cargo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Não informado</SelectItem>
                  {jobPositions.map((item) => (
                    <SelectItem key={item.id} value={item.id}>
                      {item.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
  
            <div>
              <Label>Grupo de Avaliação</Label>
              <Select
                value={value.worker_group_id ?? "__none__"}
                onValueChange={(nextValue) =>
                  onChange(clearOtherCuts(value, "worker_group_id", nextValue === "__none__" ? "" : nextValue))
                }
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Selecione o grupo de avaliação" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Não informado</SelectItem>
                  {workerGroups.map((item) => (
                    <SelectItem key={item.id} value={item.id}>
                      {item.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
  
            <div className="md:col-span-2">
              <Label>Título *</Label>
              <Input
                className="mt-1"
                value={value.title}
                onChange={(e) => handleTitleChange(e.target.value)}
                placeholder="Ex.: Assédio moral - Geral"
              />
              <p className="mt-1 text-xs text-muted-foreground">
                O título é sugerido automaticamente como “Nome do Risco - Corte”, mas pode ser editado manualmente.
              </p>
            </div>
  
            <div className="md:col-span-2">
              <Label>Descrição</Label>
              <Textarea
                className="mt-1 min-h-[120px]"
                value={value.description ?? ""}
                onChange={(e) => updateField("description", e.target.value || null)}
                placeholder="Descreva o contexto do risco no inventário."
              />
            </div>
          </div>
        </div>
  
        <div className="shrink-0 border-t bg-background px-6 py-4">
          <DialogFooter className="flex-row justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button onClick={onSubmit} disabled={saving}>
              {saving ? "Salvando..." : mode === "create" ? "Criar item" : "Salvar alterações"}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
